import { useRef, useState } from 'react';
import { Roles } from '@touristation/shared';
import { useAuth } from '../auth/AuthProvider';
import { useLogo, useSetSetting } from '../api/hooks';
import { apiUpload } from '../api/client';
import { useT } from '../i18n/LangProvider';
import { toast } from '../ui/toast';
import { useTweaks, type Tweaks } from '../ui/tweaks';
import { BrandMark, Icon } from '../ui/Icon';

function Choice<K extends keyof Tweaks>({ label, field, value, options, set }: {
  label: string; field: K; value: Tweaks[K];
  options: { v: Tweaks[K]; l: string }[];
  set: (k: K, v: Tweaks[K]) => void;
}) {
  return (
    <div className="field" style={{ marginBottom: 16 }}>
      <label className="label" style={{ display: 'block', marginBottom: 6 }}>{label}</label>
      <div className="row" style={{ gap: 8 }}>
        {options.map((o) => (
          <button key={String(o.v)} className={`btn ${value === o.v ? 'btn--primary' : ''}`} onClick={() => set(field, o.v)}>
            {o.l}
          </button>
        ))}
      </div>
    </div>
  );
}

function LogoSettings() {
  const t = useT();
  const logo = useLogo();
  const setSetting = useSetSetting();
  const fileRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);

  async function onFile(file: File | undefined) {
    if (!file) return;
    setBusy(true);
    try {
      const { url } = await apiUpload(file);
      await setSetting.mutateAsync({ key: 'logo', value: url });
    } catch (e) {
      toast.error(`${t('Caricamento non riuscito')}: ${e instanceof Error ? e.message : t('errore')}`);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="card" style={{ padding: 20, marginBottom: 16 }}>
      <h2 className="h2" style={{ fontSize: 16, marginBottom: 12 }}>{t('Logo aziendale')}</h2>
      <input ref={fileRef} type="file" accept="image/*" hidden onChange={(e) => { void onFile(e.target.files?.[0]); e.target.value = ''; }} />
      <div className="row" style={{ gap: 16, alignItems: 'center' }}>
        <span style={{ width: 56, height: 56, borderRadius: 12, background: 'var(--bg-sunk,#f4f4f4)', display: 'grid', placeItems: 'center' }}>
          <BrandMark size={40} />
        </span>
        <div className="cluster">
          <button className="btn btn--brand btn--sm" onClick={() => fileRef.current?.click()} disabled={busy}>
            <Icon name="map" size={13} /> {busy ? t('Caricamento…') : t('Carica logo')}
          </button>
          {logo.data?.value && (
            <button className="btn btn--sm" onClick={() => setSetting.mutate({ key: 'logo', value: '' })}>{t('Ripristina default')}</button>
          )}
        </div>
      </div>
      <div className="muted" style={{ fontSize: 12, marginTop: 10 }}>{t('PNG o SVG con sfondo trasparente, quadrato. Viene mostrato nella sidebar e nel login.')}</div>
    </div>
  );
}

export function SettingsScreen() {
  const t = useT();
  const auth = useAuth();
  const [tweaks, setTweak] = useTweaks();
  const isAdmin = auth.hasRole(Roles.ADMIN);

  return (
    <div style={{ padding: 24, maxWidth: 560 }}>
      <div className="card" style={{ padding: 20, marginBottom: 16 }}>
        <h2 className="h2" style={{ fontSize: 16, marginBottom: 14 }}>{t('Aspetto')}</h2>
        <Choice label={t('Tema')} field="theme" value={tweaks.theme} set={setTweak}
          options={[{ v: 'light', l: t('Chiaro') }, { v: 'dark', l: t('Scuro') }]} />
      </div>

      {isAdmin && <LogoSettings />}

      <div className="card" style={{ padding: 20 }}>
        <h2 className="h2" style={{ fontSize: 16, marginBottom: 10 }}>Account</h2>
        <div className="muted" style={{ fontSize: 13, marginBottom: 12 }}>{auth.email} · {auth.roles.join(', ')}</div>
        <button className="btn" onClick={auth.logout}>{t('Esci')}</button>
      </div>
    </div>
  );
}
